import * as React from 'react';
import { Field, Formik, FormikProps, FormikHelpers as FormikActions } from 'formik';
import { ContactInfo } from './types';
import { ResponsiveModalContainer, SpinnerOverlay } from '@weddingspot/ws-core-components';
import TextInput from '../input-text';
import CheckboxInput from '../input-checkbox';
import { WsFormikError } from '../../utils/formik/error';

interface Props {
    show: boolean;
    contact: ContactInfo | null;

    handleClose: () => void;
    handleSubmit: (info: ContactInfo) => Promise<void>;
    findUser: (email: string) => Promise<ContactInfo | null>;
}

interface State {
    searchingEmail: boolean;
}

const DEFAULT_INITIAL_VALUES = {
    id: null,
    first_name: '',
    last_name: '',
    email: '',
    account_created: false,
    phone_number: '',
    is_primary: false,
    receive_appointments: false,
    receive_invoices: false,
    receive_approval_requests: false,
    receive_metrics_emails: false,
    access_venue_metrics: false,
};

const PHONE_MASK = '999-999-9999[ ext. 9999]';

/**
 * Modal for creating/editing contact
 */
export class ContactModal extends React.Component<Props, State> {
    constructor(props: Props) {
        super(props);

        this.renderForm = this.renderForm.bind(this);
        this.onSubmit = this.onSubmit.bind(this);
        this.state = {
            searchingEmail: false,
        };
    }

    render() {
        const { contact } = this.props;

        return (
            <div className='VenueManagement--contact-modal'>
                <Formik
                    initialValues={contact || DEFAULT_INITIAL_VALUES}
                    onSubmit={this.onSubmit}
                    render={this.renderForm}
                    enableReinitialize={true}
                />
            </div>
        );
    }

    private renderForm(formikProps: FormikProps<ContactInfo>) {
        const contact = this.props.contact;

        const closeAndReset = () => {
            this.props.handleClose();
            this.setDoneSearching();
            formikProps.resetForm();
        };

        return (
            <form
                onSubmit={(e) => {
                    // This component will be embedded in other higher order
                    // components, therefore we need to stop propagating
                    // the submit event to prevent submission of any potential
                    // ancestor forms
                    e.preventDefault();
                    e.stopPropagation();
                }}
                onReset={formikProps.handleReset}
            >
                <ResponsiveModalContainer
                    show={this.props.show}
                    handleClickModalBackground={closeAndReset}
                    handleClickModalClose={closeAndReset}
                    headerContent={
                        <h3 className='vendor-signup--abandonment--header'>
                            {!!contact ? `Edit ${contact.first_name} ${contact.last_name}'s contact info` : 'Add contact'}
                        </h3>
                    }
                >
                    <div className=''>
                        <div className=''>
                            {
                                /* Form level errors */
                                formikProps.touched && formikProps.status && (
                                    <div className='VenueManagement--form-level-error'>{formikProps.status}</div>
                                )
                            }
                            {!contact && (
                                <div className='VenueManagement--contact-modal--section'>
                                    <div className='VenueManagement--contact-modal--section--header'>
                                        Find existing user to add as contact
                                    </div>
                                    <div className='VenueManagement--row VenueManagement--contact-modal--section--body'>
                                        <div className='VenueManagement--col'>
                                            <Field name='___searchEmail' label='Email' component={TextInput} />
                                        </div>
                                        <div className='VenueManagement--col-divider-sm' />
                                        <div className='VenueManagement--contact-modal--find-user'>
                                            <SpinnerOverlay
                                                isSpinning={this.state.searchingEmail}
                                                spinnerProps={{
                                                    spinnerColor: '#47505e',
                                                    spinnerDiameter: 40,
                                                }}
                                            >
                                                <button
                                                    className='nx-button nx-button--small'
                                                    type='button'
                                                    onClick={() => {
                                                        let email = formikProps.values['___searchEmail'];
                                                        if (!!email) {
                                                            email = (email as string).trim();
                                                        }
                                                        this.setSearching();
                                                        this.props
                                                            .findUser(email)
                                                            .then((user: ContactInfo | null) => {
                                                                if (!!user) {
                                                                    formikProps.setValues(user);
                                                                } else {
                                                                    formikProps.setFieldError(
                                                                        '___searchEmail',
                                                                        `Could not find user with email "${email}"`
                                                                    );
                                                                }
                                                            })
                                                            .catch(() => {
                                                                formikProps.setFieldError(
                                                                    '___searchEmail',
                                                                    `An error occurred while searching,
                                                                     please try again later`
                                                                );
                                                            })
                                                            .then(() => {
                                                                this.setDoneSearching();
                                                            });
                                                    }}
                                                >
                                                    FIND USER
                                                </button>
                                            </SpinnerOverlay>
                                        </div>
                                    </div>
                                </div>
                            )}
                            <div className='VenueManagement--contact-modal--section'>
                                {!contact && <div className='VenueManagement--contact-modal--section--header'>Add a new contact</div>}
                                <div className='VenueManagement--contact-modal--section--body'>
                                    <div className='VenueManagement--contact-modal--user-info'>
                                        <div className='VenueManagement--row'>
                                            <div className='VenueManagement--col'>
                                                <Field name='first_name' label='First Name' component={TextInput} />
                                            </div>
                                            <div className='VenueManagement--col-divider-sm' />
                                            <div className='VenueManagement--col'>
                                                <Field name='last_name' label='Last Name' component={TextInput} />
                                            </div>
                                        </div>
                                        <div className='VenueManagement--row'>
                                            <div className='VenueManagement--col'>
                                                <Field name='email' label='Email' component={TextInput} />
                                            </div>
                                            <div className='VenueManagement--col-divider-sm' />
                                            <div className='VenueManagement--col'>
                                                <Field name='phone_number' label='Phone Number' component={TextInput} mask={PHONE_MASK} />
                                            </div>
                                        </div>
                                    </div>
                                    <div className=''>
                                        <Field
                                            name='is_primary'
                                            label='Is primary contact'
                                            component={CheckboxInput}
                                            checked={!!formikProps.values.is_primary}
                                        />
                                    </div>
                                    <div className='VenueManagement--contact-modal--permissions-container'>
                                        <div className='VenueManagement--contact-modal--header'>Permissions:</div>
                                        <div className='VenueManagement--row VenueManagement--contact-modal--inputs'>
                                            <div className='VenueManagement--col'>
                                                <Field
                                                    name='receive_appointments'
                                                    label='Receives appointments'
                                                    component={CheckboxInput}
                                                    checked={!!formikProps.values.receive_appointments}
                                                />
                                            </div>
                                            <div className='VenueManagement--col'>
                                                <Field
                                                    name='receive_metrics_emails'
                                                    label='Receives metrics emails'
                                                    component={CheckboxInput}
                                                    checked={!!formikProps.values.receive_metrics_emails}
                                                />
                                            </div>
                                        </div>
                                        <div className='VenueManagement--row'>
                                            <div className='VenueManagement--col'>
                                                <Field
                                                    name='receive_invoices'
                                                    label='Receives invoices'
                                                    component={CheckboxInput}
                                                    checked={!!formikProps.values.receive_invoices}
                                                />
                                            </div>
                                            <div className='VenueManagement--col'>
                                                <Field
                                                    name='access_venue_metrics'
                                                    label='Can access venues metrics'
                                                    component={CheckboxInput}
                                                    checked={!!formikProps.values.access_venue_metrics}
                                                />
                                            </div>
                                        </div>
                                        <div className='VenueManagement--row'>
                                            <div className='VenueManagement--col'>
                                                <Field
                                                    name='receive_approval_requests'
                                                    label='Receives approval requests'
                                                    component={CheckboxInput}
                                                />
                                            </div>
                                            <div className='VenueManagement--col'>{` `}</div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div className='VenueManagement--contact-modal--cta-container'>
                                <button className='VenueManagement--contact-modal--cancel' type='button' onClick={closeAndReset}>
                                    Cancel
                                </button>
                                <button
                                    className={`nx-button nx-button--primary nx-button--small
                                    VenueManagement--contact-modal--submit`}
                                    type='submit'
                                    onClick={() => formikProps.submitForm()}
                                    disabled={formikProps.isSubmitting}
                                >
                                    Save contact
                                </button>
                            </div>
                        </div>
                    </div>
                </ResponsiveModalContainer>
            </form>
        );
    }

    private onSubmit(data: ContactInfo, actions: FormikActions<ContactInfo>) {
        // Set submitting status to disable submits, etc.
        actions.setSubmitting(true);

        // For some reason Formik doesn't reset form level errors so do it manually
        actions.setStatus(undefined);

        this.props
            .handleSubmit(data)
            .then(() => {
                // Clean form and close modal
                this.props.handleClose();
                this.setDoneSearching();
                actions.resetForm();
            })
            .catch((err: WsFormikError<ContactInfo>) => {
                actions.setErrors(err);

                if (err.__errors__) {
                    actions.setStatus(err.__errors__);
                }
            })
            .then(() => {
                // Unset submitting status to re-enable submit buttons, etc.
                actions.setSubmitting(false);
            });
    }

    private setSearching() {
        this.setState({ searchingEmail: true });
    }

    private setDoneSearching() {
        this.setState({ searchingEmail: false });
    }
}
